/*
 * Copyright (c) 2020. FINN Sp. z o.o.
 */

package pl.finn.forms.tw1

import io.kvision.form.FormType
import io.kvision.form.check.CheckBox
import io.kvision.form.text.ImaskOptions
import io.kvision.form.text.PatternMask
import io.kvision.form.text.Text
import io.kvision.html.*
import io.kvision.panel.SimplePanel
import io.kvision.panel.TabPosition
import io.kvision.panel.tab
import io.kvision.panel.tabPanel
import io.kvision.state.bind
import io.kvision.utils.px
import pl.finn.forms.tw1.Tw1Model.formularzStore
import pl.finn.forms.utils.Components
import pl.finn.forms.utils.FormReadingUtils
import pl.finn.forms.utils.form.FormComponents
import pl.finn.forms.utils.form.extendedPanel
import pl.finn.forms.utils.model.Addresses
import pl.finn.forms.utils.model.Person

class Tw1Panel : SimplePanel() {

  private val formComponents = FormComponents()

  val TW1_5_10693 = "http://crd.gov.pl/wzor/2021/06/15/10693/"
//  val TW1_5_12139 = "http://crd.gov.pl/wzor/2023/02/13/12139/"

  init {
    bind(formularzStore) { state ->
      main {
        margin = 15.px
        id = "peupcontent"
        state.bladZapisu?.let {
          div(it, className = "alert alert-danger") {
            role = "alert"
          }
        }
        when (state.widok) {
          FormularzWidok.EDYCJA, FormularzWidok.NOWY -> {
            if (state.form != null) {
              extendedPanel<DocumentTreeNode>(
                type = FormType.INLINE, className = "mx-2 mx-md-5 mt-3"
              ) {
                val components = Components(this)
                val address = Addresses(this)
                val person = Person(this)
                add(formComponents.createButtonPanel(state, this@extendedPanel))
                h1(
                  "Tytuł wykonawczy stosowany w egzekucji należności pieniężnych",
                  className = "h4 text-center"
                ) {
                  tabindex = -1
                }
                tabPanel(TabPosition.TOP, className = "nav-pills") {
                  id = "pills-tabs"
                  tab("Dane referencyjne") {
                    setAttribute("data-finn-toggle", "pill")
                    add(
                      components.createSelect(
                        state.form.kodUrzedu,
                        "Kod organu egzekucyjnego (tożsamy z kodem Urzędu Skarbowego)",
                        Tw1Data.listOfTaxOffices,
                        true
                      ).field
                    )
                    add(
                      components.createInput(
                        state.form.sygnatura,
                        "Numer tytułu wykonawczego",
                        "Należy zawsze wpisać numer pierwotnego tytułu wykonawczego"
                      ).field
                    )
                    add(
                      components.createDateTime(
                        state.form.dataWystawienia,
                        "Data wystawienia tytułu wykonawczego",
                        "Należy wskazać datę wystawienia tytułu wykonawczego w zależności od wybranego rodzaju dokumentu:<br/> • pierwotny TW => data wystawienia TW,<br/> • zmieniony TW => data sporządzania zmienionego TW,<br/> • dalszy TW => data wystawienia dotychczasowego TW,<br/> • ponowny TW => data wystawienia dotychczasowego TW,<br/>"
                      ).field
                    )
                    add(
                      components.createSelect(
                        state.form.rodzajDokumentu,
                        "Rodzaj dokumentu",
                        Tw1Data.listOfTitles
                      ).field
                    )

                    val nestedElements = ArrayList<Any>()
                    val firstTWDataElements = ArrayList<Any>()
                    nestedElements.add(
                      components.createFieldset(
                        "Dane pierwotnego tytułu wykonawczego"
                      )
                    )
                    firstTWDataElements.add(
                      components.createInput(
                        state.form.nazwaUrzedu,
                        "Nazwa Organu Egzekucyjnego, w którym znajduje się pierwotny tytuł wykonawczy"
                      )
                    )
                    firstTWDataElements.add(
                      components.createFieldset(
                        "Adres Organu Egzekucyjnego, w którym znajduje się pierwotny tytuł wykonawczy"
                      )
                    )
                    firstTWDataElements.add(address.createPolandAddress(state.form.adresOrganuEgzekucyjnego))
                    firstTWDataElements.add(
                      components.createDateTime(
                        state.form.dataNadaniaKlauzuli,
                        "Data nadania klauzuli pierwotnego tytułu wykonawczego"
                      )
                    )
                    nestedElements.add(firstTWDataElements)
                    nestedElements.add(
                      components.createInput(
                        state.form.numerPorzadkowy,
                        "Numer porządkowy dalszego tytułu wykonawczego",
                        requirements = FormReadingUtils.checkValidation("positiveInteger")
                      )
                    )
                    nestedElements.add(
                      components.createSelect(
                        state.form.celWydania,
                        "Cel wydania dalszego tytułu wykonawczego",
                        Tw1Data.listOfAims
                      )
                    )
                    nestedElements.add(
                      components.createDateTime(
                        state.form.dataWydaniaDalszegoTW,
                        "Data wydania dalszego tytułu wykonawczego"
                      )
                    )
                    nestedElements.add(
                      components.createSelect(
                        state.form.dotyczyDokumentu,
                        "Dalszy tytuł wykonawczy dotyczy",
                        Tw1Data.listOfRelates
                      )
                    )
                    nestedElements.add(
                      components.createInput(
                        state.form.kosztyEgzekucyjne,
                        "Koszty egzekucyjne",
                        requirements = FormReadingUtils.checkValidation("TKwota2Nieujemna")
                      )
                    )
                    val collapsedDtwDiv = Div(className = "collapse") { id = "collapseFinnDtw" }
                    val collapseExecutive = components.createCollapseCheckbox(
                      state.form.dtw,
                      "Dalszy tytuł wykonawczy",
                      "collapseFinnDtw",
                      nestedElements,
                      collapsedDtwDiv
                    ).field
                    add(collapseExecutive)
                    components.readComponentsList(nestedElements, collapsedDtwDiv)
                    components.changeElementChildrenStatus(nestedElements, (collapseExecutive as CheckBox).value)
                    add(collapsedDtwDiv)
                    val reAnnotationElements = ArrayList<Any>()
                    reAnnotationElements.add(
                      components.createDateTime(
                        state.form.dataWystawieniaPonownegoTW,
                        "Tytuł wykonawczy / zmieniony tytuł wykonawczy został ponownie wydany w dniu"
                      )
                    )
                    reAnnotationElements.add(
                      components.createInput(
                        state.form.nazwaWierzyciela,
                        "w związku z postanowieniem"
                      )
                    )
                    reAnnotationElements.add(
                      components.createInput(
                        state.form.sygnaturaPostanowienia,
                        "numer (sygnatura) postanowienia wierzyciela"
                      )
                    )
                    reAnnotationElements.add(
                      components.createDateTime(
                        state.form.dataWydaniaPostanowienia,
                        "z dnia"
                      )
                    )
                    reAnnotationElements.add(
                      components.createSelect(
                        state.form.rodzajUtraconegoDokumentu,
                        "o utracie",
                        Tw1Data.listOfRelates
                      )
                    )
                    val collapsedReAnnotationDiv = Div(className = "collapse") { id = "collapseFinnAnnotation" }
                    val collapseAnnotation = components.createCollapseCheckbox(
                      state.form.adnotacja,
                      "Adnotacja dotycząca ponownie wydanego tytułu wykonawczego",
                      "collapseFinnAnnotation",
                      reAnnotationElements,
                      collapsedReAnnotationDiv
                    ).field
                    add(collapseAnnotation)
                    components.readComponentsList(reAnnotationElements, collapsedReAnnotationDiv)
                    components.changeElementChildrenStatus(reAnnotationElements, (collapseAnnotation as CheckBox).value)
                    add(collapsedReAnnotationDiv)
                  }
                  tab("Zobowiązany") {
                    setAttribute("data-finn-toggle", "pill")
                    val obligatedMap = HashMap<String, Any?>()
                    val naturalPerson =
                      person.createNaturalPerson(state.form.zobowiazanyOsobaFizyczna, "Long", "Osoba fizyczna")
                    val obligatedPhysical = person.createdObligatedPersons(
                      state.form.zobowiazanyOsobaFizycznaZobowiazanyPierwotny,
                      state.form.zobowiazanyOsobaFizycznaZobowiazanyPierwotnyData
                    )
                    val obligatedPhysicalCollapse: Div = Div()
                    components.readComponentsList(obligatedPhysical, obligatedPhysicalCollapse)
                    (naturalPerson[1] as ArrayList<Any>).add(arrayListOf(obligatedPhysicalCollapse))

                    val nonPhysicalPerson =
                      person.createNonPhysicalPerson(state.form.zobowiazanyOsobaNiefizyczna, "Long")
                    val partners = person.createPartners(
                      state.form.zobowiazanyOsobaNiefizycznaWspolnicy,
                      state.form.zobowiazanyOsobaNiefizycznaWspolnicyData
                    )
                    val obligated = person.createdObligatedPersons(
                      state.form.zobowiazanyOsobaNiefizycznaZobowiazanyPierwotny,
                      state.form.zobowiazanyOsobaNiefizycznaZobowiazanyPierwotnyData
                    )
                    val partnersCollapse: Div = partners[1] as Div
                    val obligatedCollapse: Div = Div()
//                    components.readComponentsList(partners[2], partnersCollapse)
//                    components.readComponentsList(partners[3], partnersCollapse)
                    components.readComponentsList(obligated, obligatedCollapse)
                    (nonPhysicalPerson[1] as ArrayList<Any>).add(partners[0])
                    (nonPhysicalPerson[1] as ArrayList<Any>).add(arrayListOf(partnersCollapse))
                    (nonPhysicalPerson[1] as ArrayList<Any>).add(arrayListOf(obligatedCollapse))

                    val spouses = ArrayList<Any>()
                    spouses.add(
                      components.createFieldset(
                        "Małżonkowie odpowiedzialni solidarnie"
                      )
                    )
                    val spousesArray = ArrayList<Any>()
                    spousesArray.add(
                      person.createNaturalPerson(
                        state.form.zobowiazanyMalzonkowie[0],
                        "Long",
                        "Zestaw danych identyfikacyjnych małżonka solidarnego"
                      )
                    )
                    spousesArray.add(
                      person.createNaturalPerson(
                        state.form.zobowiazanyMalzonkowie[1],
                        "Long",
                        "Zestaw danych identyfikacyjnych małżonka solidarnego"
                      )
                    )

                    val obligatedSpouses = person.createdObligatedPersons(
                      state.form.zobowiazanyMalzonekZobowiazanyPierwotny,
                      state.form.zobowiazanyMalzonekZobowiazanyPierwotnyData
                    )
                    val obligatedSpousesCollapse: Div = Div()
                    components.readComponentsList(obligatedSpouses, obligatedSpousesCollapse)
                    spousesArray.add(arrayListOf(obligatedSpousesCollapse))
                    spouses.add(spousesArray)

                    obligatedMap["0"] = naturalPerson
                    obligatedMap["1"] = nonPhysicalPerson
                    obligatedMap["2"] = spouses
                    val radio = components.createRadioGroup(
                      state.form.zobowiazany,
                      "Dane zobowiązanego/zobowiązanych",
                      Tw1Data.optionsOfObligated,
                      obligatedMap
                    )

                    add(radio.field)
                    components.readComponentsList(obligatedMap, this, radio)
                  }
                  tab("Należność") {

                    setAttribute("data-finn-toggle", "pill")
                    val remittance = ArrayList<Any>()
                    val remittanceData = ArrayList<Any>()
                    remittance.add(
                      components.createFieldset(
                        "Dane dotyczące należności pieniężnych"
                      )
                    )
                    remittanceData.add(
                      components.createTextArea(
                        state.form.naleznoscAktNormatywny,
                        "Oznaczenie przepisu prawa, z którego wynika należność pieniężna (ze słownika podstaw prawnych)",
                        tooltipMessage = "np. Podatek rolny-art. 6a ustawy z 15.11.1984 o pod. rolnym (t.j. Dz.U.1993.94.431)"
                      )
                    )
                    remittanceData.add(
                      components.createTextArea(
                        state.form.naleznoscRodzajNaleznosci,
                        "Rodzaj należności pieniężnej <br> <a target=\"_blank\" href=\"http://www.is-szczecin.pl/egzadm/podstawyprawne1.php\"><b>Słownik podstaw prawnych</b></a>",
                        tooltipMessage = "np. Podatek rolny"
                      )
                    )

                    val naleznoscRodzajNaleznosciKodPodstawyPrawnejComponent = components.createInput(
                      state.form.naleznoscRodzajNaleznosciKodPodstawyPrawnej,
                      "Kod podstawy prawnej (ze słownika podstaw prawnych)",
                      tooltipMessage = "np. CL 1AK9320090301 (kod może zawierać min. 12 znaków a  max. 30 znaków)",
                      requirements = arrayOf("[a-zA-Z0-9 ]{12,30}", "kod musi zawierać min. 12 znaków a  max. 30 znaków")
                    )

                    (naleznoscRodzajNaleznosciKodPodstawyPrawnejComponent.field as Text).apply {
                      maskOptions = ImaskOptions(
                        pattern = PatternMask(
                          pattern = "a[a]{ }0********[*][*][*][*][*][*][*][*][*][*][*][*][*][*][*][*][*][*][*]",
                          lazy = false,
                          eager = null,
                          placeholderChar = ' ',
                          definitions = null,
                          blocks = null,
                        )
                      )
                    }
                    remittanceData.add(
                      naleznoscRodzajNaleznosciKodPodstawyPrawnejComponent
                    )
                    remittanceData.add(
                      components.createSelect(
                        state.form.naleznoscRodzajNaleznosciRodzaj,
                        "Kategoria należności",
                        Tw1Data.listOfReceivable
                      )
                    )

                    val legalBasis = HashMap<String, Any?>()
                    legalBasis["0"] =
                      components.createInput(
                        state.form.naleznoscObowiazekZMocyPrawa,
                        "Identyfikacja podstawy prawnej obowiązku wynikającego z mocy prawa"
                      )
                    legalBasis["1"] =
                      components.createInput(
                        state.form.naleznoscObowiazekInnyDokument,
                        "Identyfikacja podstawy prawnej obowiązku wynikającego z dokumentu, o którym mowa w art. 3a § 1 ustawy"
                      )
                    legalBasis["2"] = arrayListOf(
                      components.createDateTime(
                        state.form.naleznoscOrzeczenieDataWydania,
                        "Data wydania orzeczenia"
                      ),
                      components.createInput(
                        state.form.naleznoscObowiazekOrzeczenie,
                        "Identyfikacja podstawy prawnej obowiązku wynikającego z orzeczenia"
                      )
                    )
                    val radio = components.createRadioGroup(
                      state.form.naleznoscObowiazek,
                      "Podstawa prawna obowiązku",
                      Tw1Data.optionsOfLegalBasis,
                      legalBasis
                    )

                    remittanceData.add(radio)
                    remittanceData.add(legalBasis)

                    val legalProvision = HashMap<String, Any?>()
                    legalProvision["0"] =
                      components.createInput(
                        state.form.naleznoscDoreczeniaBrakUpomnienia,
                        "Podstawa prawna braku obowiązku doręczenia upomnienia"
                      )
                    val reminder = ArrayList<Any>()
                    val reminderData = ArrayList<Any>()
                    reminder.add(
                      components.createFieldset(
                        "Upomnienie"
                      )
                    )
                    reminderData.add(
                      components.createInput(
                        state.form.naleznoscDoreczeniaKosztyUpomnienia,
                        "Kwota kosztów upomnienia",
                        tooltipMessage = "Wymagana wartość: <b>xx.xx</b>",
                        requirements = FormReadingUtils.checkValidation("TKwota2Nieujemna")
                      )
                    )

                    val reminderDeliveryDataLabel = "Data doręczenia upomnienia"
                    val reminderDeliveryDataList = person.createReminderDeliveryData(state.form.naleznoscDoreczeniaDataDoreczenia, reminderDeliveryDataLabel)
                    val divReminderDeliveryData = Div() { id = "reminderDeliveryData" }
                    reminderData.add(divReminderDeliveryData)
                    reminderData.add(
                      arrayListOf(
                        reminderDeliveryDataList,
                        person.createButtonReminderDeliveryDataGroup(state.form.naleznoscDoreczeniaDataDoreczenia, divReminderDeliveryData, reminderDeliveryDataLabel)
                      )
                    )
                    reminder.add(reminderData)
                    legalProvision["1"] = reminder
                    val notification = ArrayList<Any>()
                    val notificationData = ArrayList<Any>()
                    notification.add(
                      components.createFieldset(
                        "Powiadomienie"
                      )
                    )
                    notificationData.add(
                      components.createFieldset(
                        "Oznaczenie organu odpowiedzialnego za rozpatrzenie administracyjnej kary pieniężnej lub grzywny administracyjnej"
                      )
                    )
                    notificationData.add(person.createSubject(state.form.namespaceURI, state.form.naleznoscOgranOdpowiedzialny))

                    val collapsedAdditionalInfDiv = Div(className = "collapse") { id = "collapseFinnAdditionalInf" }
                    val additionalInf = person.createSubject(state.form.namespaceURI, state.form.naleznoscOgranUdzielajacyInformacjiData)

                    val collapseNotification = components.createCollapseCheckbox(
                      state.form.naleznoscOgranUdzielajacyInformacji,
                      "Oznaczenie organu, który może udzielić dodatkowych informacji dotyczących administracyjnej kary pieniężnej lub grzywny administracyjnej lub możliwości odwołania się od obowiązku zapłaty lub zaskarżenia decyzji w sprawie ich nałożenia",
                      "collapseAdditionalInf",
                      additionalInf,
                      collapsedAdditionalInfDiv
                    )

                    notificationData.add(collapseNotification)
                    components.changeElementChildrenStatus(
                      additionalInf,
                      (collapseNotification.field as CheckBox).value
                    )
                    notificationData.add(additionalInf)
                    notification.add(notificationData)

                    legalProvision["2"] = notification
                    val radioProvision = components.createRadioGroup(
                      state.form.naleznoscDoreczenia,
                      "Oznaczenie przepisu prawa, z którego wynika należność pieniężna (ze słownika podstaw prawnych)",
                      Tw1Data.optionsOfDelivered,
                      legalProvision
                    )

                    remittanceData.add(radioProvision)
                    remittanceData.add(legalProvision)

                    val withInterest = ArrayList<Any>()
                    val withInterestData = ArrayList<Any>()
                    withInterest.add(
                      components.createFieldset("Pobiera się odsetki we wskazanej stawce")
                    )
//                    withInterestData.add(Label("<a target=\"_blank\" href=\"https://kalkulatory.gofin.pl/Kalkulatory-odsetek,12.html\"><b>Kalkulatory odsetek</b></a>", rich = true))
                    withInterestData.add(
                      components.createSelect(
                        state.form.zobowiazanieOdsetkiRodzaj,
                        "Rodzaj odsetek",
                        Tw1Data.listOfInterest
                      )
                    )
                    withInterestData.add(address.createPositions(state.form.zobowiazanieOdsetkiPozycja, "Pozycja", true))
                    val rateData = HashMap<String, Any?>()
                    rateData["0"] = components.createInput(
                      state.form.zobowiazanieOdsetkiStawka,
                      "Stawka odsetek"
                    )
                    rateData["1"] = components.createInput(
                      state.form.zobowiazanieOdsetkiPodwyzszonaStawka,
                      "Podwyższona stawka odsetek"
                    )
                    val radioRate = components.createRadioGroup(
                      state.form.zobowiazanieOdsetki,
                      "",
                      Tw1Data.optionsOfRates,
                      rateData
                    )
                    withInterestData.add(radioRate)
                    withInterestData.add(rateData)
                    withInterest.add(withInterestData)
                    val obligationData = HashMap<String, Any?>()
                    obligationData["0"] =
                      address.createPositions(state.form.zobowiazanieBrakOdsetekPozycja, "Nie pobiera się odsetek", false)
                    obligationData["1"] = withInterest
                    val radioObligation = components.createRadioGroup(
                      state.form.zobowiazanie,
                      "Zobowiązanie",
                      Tw1Data.optionsOfObligation,
                      obligationData
                    )
                    remittanceData.add(radioObligation)
                    remittanceData.add(obligationData)

                    remittanceData.add(
                      components.createTextArea(
                        state.form.innaPodstawaPrawna,
                        "Pełen tekst innej podstawy prawnej egzekucji",
                        isRequired = false
                      )
                    )
                    remittanceData.add(
                      components.createFieldset(
                        "Informacje dotyczące należności pieniężnej"
                      )
                    )
                    remittanceData.add(
                      arrayListOf(
                        components.createCheckbox(
                          state.form.odpowiedzialnoscObejmujeMajatekWspolny,
                          "odpowiedzialność za należność pieniężną i odsetki z tytułu niezapłacenia jej w terminie obejmuje również majątek wspólny zobowiązanego i jego małżonka",
                          isRequired = false
                        ),
                        components.createCheckbox(
                          state.form.obowiazekScisleZwiazanyZeZobowiazanym,
                          "obowiązek jest ścieśle związany ze zobowiązanym",
                          isRequired = false
                        )
                      )
                    )

                    val nZElements = ArrayList<Any>()
                    nZElements.add(
                      components.createFieldset(
                        "Zabezpieczenie"
                      )
                    )
                    nZElements.add(
                      arrayListOf(
                        components.createSelect(
                          state.form.naleznoscZabezpieczenia.hipotekaZastaw,
                          "Rodzaj zabezpieczenia należności pieniężnej",
                          Tw1Data.listOfCashReceivableCollateral
                        ),
                        components.createDateTime(
                          state.form.naleznoscZabezpieczenia.dataPowstania,
                          "Data powstania zabezpieczenia"
                        )
                      )
                    )
                    val collapsedNZDiv = Div(className = "collapse") { id = "collapseFinnNZ" }
                    val collapsedNZExecutive = components.createCollapseCheckbox(
                      state.form.naleznoscZabezpieczenia.posiadaZabezpieczenie,
                      "Występuje zabezpieczenie należności pieniężnej hipoteką przymusową lub zastawem",
                      "collapseFinnNZ",
                      nZElements,
                      collapsedNZDiv
                    )
                    remittanceData.add(collapsedNZExecutive)
                    components.readComponentsList(nZElements, collapsedNZDiv)
                    components.changeElementChildrenStatus(nZElements, (collapsedNZExecutive.field as CheckBox).value)
                    remittanceData.add(nZElements)

                    remittance.add(remittanceData)
                    components.readComponentsList(remittance, this)
                  }
                  tab("Wierzyciel") {
                    setAttribute("data-finn-toggle", "pill")
                    val creditor = ArrayList<Any>()
                    val creditorData = ArrayList<Any>()
                    creditor.add(
                      components.createFieldset(
                        "Oznaczenie i wniosek wierzyciela"
                      )
                    )
                    creditorData.add(
                      components.createInput(
                        state.form.wierzycielKodWierzyciela,
                        "Kod wierzyciela (ze słownika wierzycieli ustawowych)" +
                            "<br> <a target=\"_blank\" href=\"http://www.is-szczecin.pl/egzadm/wierzyciele1_prev.php\"><b>Słownik wierzycieli</b></a>",
                        tooltipMessage = "np. IAPT01 (kod dla BESKIDZKA OKRĘGOWA IZBA APTEKARSKA)",
                        requirements = FormReadingUtils.checkValidation("TKodWierzyciela")
                      )
                    )
                    creditorData.add(components.createInput(state.form.wierzycielNazwa, "Nazwa wierzyciela"))
                    creditorData.add(components.createFieldset("Adres siedziby wierzyciela"))
                    creditorData.add(address.createPolandAddress(state.form.wierzycielAdres))
                    creditorData.add(components.createFieldset("Identyfikator wierzyciela"))
                    creditorData.add(
                      arrayListOf<Any>(
                        components.createInput(
                          state.form.wierzycielNip,
                          "NIP",
                          "Identyfikator podatkowy NIP wierzyciela, a jeżeli wierzyciel go nie posiada - urzędu obsługującego wierzyciela lub jednostki samorządu terytorialnego, której organ jest wierzycielem",
                          isRequired = false,
                          requirements = FormReadingUtils.checkValidation("TNrNIP")
                        ),
                        components.createInput(
                          state.form.wierzycielRegon,
                          "REGON",
                          "REGON wierzyciela, a jeżeli wierzyciel go nie posiada - urzędu obsługującego wierzyciela lub jednostki samorządu terytorialnego, której organ jest wierzycielem",
                          isRequired = false,
                          requirements = FormReadingUtils.checkValidation("TNrREGON")
                        )
                      )
                    )
                    creditorData.add(components.createInput(state.form.wierzycielNrTelefonu, "Numer telefonu", isRequired = false, requirements = FormReadingUtils.checkValidation("TNrTelefonu")))
                    creditorData.add(
                      components.createInput(
                        state.form.wierzycielEmail,
                        "Adres e-mail",
                        requirements = FormReadingUtils.checkValidation("TAdresEmail"),
                        isRequired = false
                      )
                    )
                    creditorData.add(
                      components.createInput(
                        state.form.wierzycielSkrytkaEPuap,
                        "Adres skrzynki ePUAP",
                        requirements =
                        if(state.form.namespaceURI == TW1_5_10693)
                          FormReadingUtils.checkValidation("TSkrytkaEPuap_10693")
                        else
                          FormReadingUtils.checkValidation("TSkrytkaEPuap_12139"),
                        isRequired = false
                      )
                    )
                    creditorData.add(components.createFieldset("Nazwa, adres siedziby i numer rachunku bankowego podmiotu, któremu należy przekazać wyegzekwowaną należność pieniężną"))
                    val bankAcoount = components.createInput(
                      state.form.beneficjentNrRachunkuPl,
                      "Numer rachunku bankowego beneficjenta",
                      "CCAAAAAAAABBBBBBBBBBBBBBBB (26 cyfr)<br/> gdzie:<br/> CC to suma kontrolna (2 cyfry) <br/> AAAAAAAA to numer rozliczeniowy jednostki organizacyjnej banku (8 cyfr) <br/> BBBBBBBBBBBBBBBB to numer rachunku klienta w banku (16 cyfr). <br/><br/>",
                      isRequired = false
                    )
                    bankAcoount.field.input.setAttribute("placeholder", "__ ____ ____ ____ ____ ____ ____")
                    bankAcoount.field.input.setAttribute("data-slots", "_")
                    bankAcoount.field.input.setAttribute("data-accept", "\\d")
                    bankAcoount.field.input.setAttribute("size", "32")
                    bankAcoount.field.input.setAttribute("onfocus", "FormUtils.applyDataMask(this);")
                    creditorData.add(
                      arrayListOf<Any>(
                        components.createInput(state.form.beneficjentNazwa, "Nazwa beneficjenta"),
                        components.createFieldset("Adres siedziby beneficjenta"),
                        address.createPolandAddress(state.form.beneficjentAdres),
                        components.createInput(state.form.beneficjentNrTelefonu, "Numer telefonu", isRequired = false, requirements = FormReadingUtils.checkValidation("TNrTelefonu")),
                        components.createInput(
                          state.form.beneficjentEmail,
                          "Adres e-mail",
                          requirements = FormReadingUtils.checkValidation("TAdresEmail"),
                          isRequired = false
                        ),
                        bankAcoount
                      )
                    )

                    creditorData.add(components.createFieldset("Osoba upoważniona do działania w imieniu wierzyciela"))
                    creditorData.add(
                      arrayListOf<Any>(
                        components.createInput(
                          state.form.osobaUpowaznionaImie,
                          "Imię osoby upoważnionej",
                          tooltipMessage = "W polu imię/imiona osoby upoważnionej należy wpisać dokładnie takie same dane, jak w danych certyfikatu."
                        ),
                        components.createInput(
                          state.form.osobaUpowaznionaNazwisko,
                          "Nazwisko osoby upoważnionej"
                        ),
                        components.createInput(
                          state.form.osobaUpowaznionaStanowisko,
                          "Stanowisko służbowe osoby upoważnionej"
                        ),
                      )
                    )
                    creditor.add(creditorData)
                    components.readComponentsList(creditor, this)
                  }
                  tab("Załączniki") {
                    setAttribute("data-finn-toggle", "pill")
                    components.readComponentsList(
                      formComponents.addAttachments(state, this@extendedPanel), this
                    )
                    if (!state.zalaczniki.isNullOrEmpty()) {
                      components.readComponentsList(
                        formComponents.createAttachmentsTable(state, this@extendedPanel), this
                      )
                    }
                    val concersData = HashMap<String, Any?>()
                    val assetsLabel = "posiadanego przez zobowiązanego składnika majątkowego lub źródła jego dochodu"
                    val parents = person.createAnAsset(state.form.skladnikMajatku, assetsLabel)
                    val div = Div() { id = "anAsset" }
                    concersData["0"] = arrayListOf<Any>(
                      components.createFieldset("Składniki majątkowe"),
                      div,
                      arrayListOf(
                        parents,
                        person.createButtonAssetGroup(state.form.skladnikMajatku, div, assetsLabel)
                      )
                    )
                    concersData["1"] = arrayListOf<Any>(
                      components.createFieldset("Siedziby lub miejsca zamieszkania dłużnika zobowiązanego, który jest obciążony prawem majątkowym względem zobowiązanego"),
                      arrayListOf(
                        address.createAddress(
                          state.form.adresDluznikaZobowiazanegoAdres,
                          state.form.adresDluznikaZobowiazanegoAdresData
                        ),
                        components.createInput(state.form.adresDluznikaZobowiazanegoNrTelefonu, "Numer telefonu", requirements = FormReadingUtils.checkValidation("TNrTelefonu"))
                      )
                    )
                    concersData["2"] = arrayListOf<Any>(
                      components.createFieldset("Daty wysłania przez centralne biuro łącznikowe wniosku o udzielenie informacji lub powiadomienie do państwa członkowskiego lub państwa trzeciego oraz numeru referencyjnego tego wniosku"),
                      arrayListOf(
                        components.createDateTime(
                          state.form.dataNumerWnioskuDataWyslania,
                          "Data wysłania wniosku lub powiadomienia"
                        ),
                        components.createInput(state.form.dataNumerWnioskuNrRefWniosku, "Numer referencyjny wniosku")
                      )
                    )
                    concersData["3"] = arrayListOf<Any>(
                      components.createFieldset("Ujawnionego po umorzeniu postępowania egzekucyjnego na podstawie art. 59 § 2 składnika majątkowego lub źródła dochodu zobowiązanego oraz numeru i daty wydania postanowienia o umorzeniu postępowania egzekucyjnego"),
                      arrayListOf(
                        components.createDateTime(
                          state.form.postanowienieUmorzeniaDataWydania,
                          "Data wysłania wniosku lub powiadomienia"
                        ),
                        components.createInput(
                          state.form.postanowienieUmorzeniaNrPostanowienia,
                          "Numer referencyjny wniosku"
                        ),
                        components.createTextArea(state.form.postanowienieUmorzeniaTrescInformacji, "Treść informacj")
                      )
                    )
                    concersData["4"] = arrayListOf<Any>(
                      components.createFieldset("Imienia i nazwiska oraz adresu do korespondencji przedstawiciela ustawowego/opiekuna/kuratora zobowiązanego"),
                      arrayListOf(
                        components.createSelect(
                          state.form.reprezentantTypPrzedstawiciela,
                          "Typ przedstawiciela",
                          Tw1Data.listOfRepresentativeTypes
                        ),
                        components.createInput(state.form.reprezentantImie, "Imię"),
                        components.createInput(state.form.reprezentantNazwisko, "Nazwisko"),
                        address.createAddress(
                          state.form.reprezentantAdres,
                          state.form.reprezentantAdresData
                        ),
                        components.createInput(state.form.reprezentantNrTelefonu, "Numer telefonu", requirements = FormReadingUtils.checkValidation("TNrTelefonu")),
                        components.createInput(
                          state.form.reprezentantEmail,
                          "Adres e-mail",
                          requirements = FormReadingUtils.checkValidation("TAdresEmail")
                        ),
                        components.createInput(
                          state.form.reprezentantSkrytkaEPuap,
                          "Adres skrzynki ePUAP",
                          requirements =
                          if(state.form.namespaceURI == TW1_5_10693)
                            FormReadingUtils.checkValidation("TSkrytkaEPuap_10693")
                          else
                            FormReadingUtils.checkValidation("TSkrytkaEPuap_12139"),
                          isRequired = false
                        )
                      )
                    )
                    concersData["5"] = arrayListOf<Any>(
                      components.createFieldset("Imienia i nazwiska oraz adresu do korespondencji zarządcy przedsiębiorstwa w spadku"),
                      arrayListOf(
                        components.createInput(state.form.zarzadcaWSpadkuImie, "Imię"),
                        components.createInput(state.form.zarzadcaWSpadkuNazwisko, "Nazwisko"),
                        address.createAddress(
                          state.form.zarzadcaWSpadkuAdres,
                          state.form.zarzadcaWSpadkuAdresData
                        ),
                        components.createInput(state.form.zarzadcaWSpadkuNrTelefonu, "Numer telefonu", requirements = FormReadingUtils.checkValidation("TNrTelefonu")),
                        components.createInput(
                          state.form.zarzadcaWSpadkuEmail,
                          "Adres e-mail",
                          requirements = FormReadingUtils.checkValidation("TAdresEmail")
                        ),
                        components.createInput(
                          state.form.zarzadcaWSpadkuSkrytkaEPuap,
                          "Adres skrzynki ePUAP",
                          requirements =
                          if(state.form.namespaceURI == TW1_5_10693)
                            FormReadingUtils.checkValidation("TSkrytkaEPuap_10693")
                          else
                            FormReadingUtils.checkValidation("TSkrytkaEPuap_12139")
                        ),
                        components.createFieldset("Dane zmarłego przedsiębiorcy"),
                        arrayListOf(
                          components.createInput(
                            state.form.daneZmarlegoPrzedsiebiorcyImie,
                            "Imię zmarłego przedsiębiorcy"
                          ),
                          components.createInput(
                            state.form.daneZmarlegoPrzedsiebiorcyNazwisko,
                            "Nazwisko zmarłego przedsiębiorcy"
                          ),
                          components.createInput(
                            state.form.daneZmarlegoPrzedsiebiorcyPesel,
                            "Numer ewidencyjny PESEL zmarłego przedsiębiorcy"
                          )
                        )
                      )
                    )
                    val enforcementLabel =
                      "majątku zobowiązanego lub większej jego części znajdującego się na terenie działania organu egzekucyjnego"
                    val enforcementAuthority =
                      person.createAnAsset(state.form.majatekZobowiazanegoNaTerenieOE, enforcementLabel)
                    val enforcement = Div() { id = "enforcementAuthority " }
                    concersData["6"] = arrayListOf<Any>(
                      components.createFieldset("Majątku zobowiązanego lub większej jego części znajdującego się na terenie działania organu egzekucyjnego"),
                      enforcement,
                      arrayListOf(
                        enforcementAuthority,
                        person.createButtonAssetGroup(
                          state.form.majatekZobowiazanegoNaTerenieOE,
                          enforcement,
                          enforcementLabel
                        )
                      )
                    )
                    val otherLabel = "Inna okoliczność"
                    val otherCircumstances = person.createAnAsset(state.form.innaOkolicznosc, otherLabel)
                    val other = Div() { id = "enforcementAuthority " }
                    concersData["7"] = arrayListOf<Any>(
                      components.createFieldset("Innych okoliczności niewskazanych w ustawie"),
                      other,
                      arrayListOf(
                        otherCircumstances,
                        person.createButtonAssetGroup(state.form.innaOkolicznosc, other, otherLabel)
                      )
                    )
                    val radio = components.createRadioGroup(
                      state.form.zakresInfomacji,
                      "Informacja dotyczy:",
                      Tw1Data.optionsOfConcers,
                      concersData,
                      isRequired = !state.zalaczniki.isNullOrEmpty()
                    )
                    add(radio.field)
                    components.readComponentsList(concersData, this, radio)
                  }
                }
                add(formComponents.createNavigationButtons())
                console.log("namespaceURI: " + state.form.namespaceURI)
              }
            }
          }
          else -> {
            // Block
          }
        }
      }
    }
  }
}
